// ABOUTME: Yandex Metrika — the one analytics service on the .ru version of the app (GA, Clarity and Mixpanel
// ABOUTME: run on .com only). Loads the counter's tag and counts every page the app shows as a view.

import config from "@/config"
import { runtimeTld } from "./domain"

/** The counter's tag: a stub collecting calls in `a` until tag.js loads and takes them over (Yandex's own snippet). */
type YmFn = ((...args: unknown[]) => void) & { a?: unknown[][]; l?: number }

declare global {
  interface Window {
    ym?: YmFn
  }
}

const TAG_URL = "https://mc.yandex.ru/metrika/tag.js"

/** The counter on a .ru host, null anywhere else or when none is configured. */
const counter = (): number | null => {
  const id = Number(config.YANDEX_METRIKA_ID)
  return runtimeTld() === "ru" && Number.isInteger(id) && id > 0 ? id : null
}

/** The page counted last, the referrer of the next one. */
let previousPage: string | null = null

export const initMetrika = (): void => {
  const id = counter()
  if (id === null || window.ym) return

  const stub: YmFn = (...args: unknown[]) => {
    stub.a = stub.a ?? []
    stub.a.push(args)
  }
  stub.l = Date.now()
  window.ym = stub

  // defer: the counter sends no view of its own when it starts — every view, the first included, is a hit from
  // logMetrikaPageView on each page change (AppShell for the sign-in screens, AppRoutes for the app's pages); one sent
  // here would count the first page twice.
  // Webvisor stays off: it records the screen, and the app's screens show health data — the reason Mixpanel's
  // session recording was turned off too (mixpanel.ts). Yandex Tag Manager stays off (disableYtm): it loads the tags
  // set up for the counter in Metrika — for this one, a Webmaster script from cdn.jsdelivr.net — and a .ru host runs
  // Metrika alone (seen on beta.trainingendurance.ru, 2026-10-10). The click map, external links and accurate bounce
  // keep their defaults (on).
  window.ym(id, "init", { defer: true, webvisor: false, disableYtm: true })

  const script = document.createElement("script")
  script.async = true
  script.src = TAG_URL
  document.head.appendChild(script)
}

/**
 * Counts the page the app shows now. Its address goes without the query and the hash — the same page as Google
 * Analytics counts on .com (ga.ts) — and the page before it is the referrer, as a browser would name it.
 *
 * The page counted last is not counted again: a redirect route (/settings → /settings/profile) moves the address in
 * its <Navigate>'s effect before AppRoutes' effect runs (react-router chunk-BV7QT456.mjs:7119, history.replaceState
 * at :329), so that run already counts the destination, and the route change brings the same address back.
 */
export const logMetrikaPageView = (): void => {
  const id = counter()
  if (id === null || !window.ym) return

  const page = window.location.origin + window.location.pathname
  if (page === previousPage) return
  window.ym(id, "hit", page, previousPage === null ? {} : { referer: previousPage })
  previousPage = page
}
